// signalsApi.js — the SignalsService client (gRPC + JSON gateway, gated by
// tenant.signals.read). Lives in its own module (not apiClient) to stay
// cycle-free: signalCatalog imports these, and apiClient imports signalCatalog —
// routing the RPCs through apiClient would close a loop. Both calls go through
// `authedFetch` (the shared `/v1/*` bearer wrapper), and IDs stay base64 strings
// end-to-end (the dashboard's wire-UUID convention), so `id` from listSignals is
// passed straight back as `signalId`.
import { authedFetch } from "./restClient.js";

function num(v) {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

// grpc-gateway's default JSON encoder emits camelCase, but accept snake_case too
// in case it ever flips to original_name=true — the same dual-shape guard
// `userInfo.js` / `permissions.js` carry (this mirrors their `pick` helper).
function pick(obj, snake, camel) {
    if (!obj) return undefined;
    return obj[camel] ?? obj[snake];
}

function adaptSignal(s) {
    const namespaces = Array.isArray(s?.namespaces) ? s.namespaces.filter((n) => typeof n === "string") : [];
    return {
        id: String(s?.id ?? ""),
        name: String(s?.name ?? ""),
        description: String(s?.description ?? ""),
        displayName: String(pick(s, "display_name", "displayName") ?? ""),
        kind: String(s?.kind ?? ""),
        namespaces,
        firstParty: Boolean(pick(s, "first_party", "firstParty") ?? false),
        deployedVersion: num(pick(s, "deployed_version", "deployedVersion")),
        deployedEnabled: Boolean(pick(s, "deployed_enabled", "deployedEnabled") ?? false),
    };
}

// GET /v1/signals → the caller tenant's readable signals, each with its
// effective per-tenant deployment (version + enabled) and catalog metadata.
// Throws with `.status` set so a 403 (missing tenant.signals.read) is
// distinguishable from a transport error by the caller.
export async function listSignals() {
    const res = await authedFetch("/v1/signals", { method: "GET" });
    if (!res.ok) {
        const err = new Error(`ListSignals ${res.status}: ${res.statusText}`);
        err.status = res.status;
        throw err;
    }
    const json = await res.json();
    const signals = Array.isArray(json.signals) ? json.signals : [];
    return signals.map(adaptSignal);
}

function adaptSignalVersion(v) {
    return {
        signalId: String(pick(v, "signal_id", "signalId") ?? ""),
        version: num(v?.version),
        source: String(v?.source ?? ""),
        sourceSha256: String(pick(v, "source_sha256", "sourceSha256") ?? ""),
        deployed: Boolean(v?.deployed ?? false),
    };
}

// POST /v1/signals:list-versions → a signal's versions (with source), newest
// first. `signalId` is the base64 id from listSignals. `version = 0` +
// `deployedOnly = false` returns all versions; the definition panel takes the
// newest as the source to display.
export async function listSignalVersions(signalId, { version = 0, deployedOnly = false } = {}) {
    const res = await authedFetch("/v1/signals:list-versions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signalId, version, deployedOnly }),
    });
    if (!res.ok) {
        const err = new Error(`ListSignalVersions ${res.status}: ${res.statusText}`);
        err.status = res.status;
        throw err;
    }
    const json = await res.json();
    const versions = Array.isArray(json.versions) ? json.versions : [];
    return versions.map(adaptSignalVersion);
}
